import { useState, useRef, useEffect } from 'react'
import { Loader2, CheckCircle2, AlertCircle, X, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { cn } from '@/lib/utils'

/**
 * Generic dialog for any long-running operation that streams NDJSON progress
 * events. Used for share/seed/version delete + bulk prune. Each caller
 * supplies an `interpretEvent` callback that translates the stream's event
 * shape into a uniform `Interpretation`:
 *
 *   - log line (✓ / · / ✗) to append
 *   - current step text (overrides previous "current")
 *   - progress numerator/denominator
 *   - terminal status ('done' | 'error' with detail) — closes the stream
 *
 * The dialog owns the fetch + reader loop + UI state machine.
 */

export interface Interpretation {
  log?: { kind: 'info' | 'ok' | 'error'; text: string }
  current?: string | null
  progress?: { done: number; total: number }
  // Terminal: emit a 'done' with summary, or 'error' with phase/message.
  terminal?:
    | { kind: 'done'; summary?: string }
    | { kind: 'error'; phase: string; message: string }
}

type Phase = 'idle' | 'running' | 'done' | 'error'

interface LogEntry {
  kind: 'info' | 'ok' | 'error'
  text: string
}

export interface LongRunningOpDialogProps {
  open: boolean
  onClose: () => void
  onComplete?: () => void
  /** Operation title shown in the dialog header. */
  title: string
  /** Description below the title — explains what the op does. */
  description: string
  /** Verb on the action button before the op starts. e.g. "Delete share". */
  confirmLabel: string
  /** Brief warning shown in the idle pane (red Alert). Optional. */
  warning?: string
  /** Method + URL + optional body for the streaming POST/DELETE call. */
  request: { method: string; url: string; body?: string | null }
  /** Translate one event from the stream into log/progress/terminal updates. */
  interpretEvent: (ev: unknown) => Interpretation
  /** Skip the idle confirm pane and start the op as soon as the dialog opens.
   *  Used when the caller already confirmed (e.g. the create form). */
  autoStart?: boolean
}

export function LongRunningOpDialog({
  open,
  onClose,
  onComplete,
  title,
  description,
  confirmLabel,
  warning,
  request,
  interpretEvent,
  autoStart,
}: LongRunningOpDialogProps) {
  const [phase, setPhase] = useState<Phase>('idle')
  const [log, setLog] = useState<LogEntry[]>([])
  const [current, setCurrent] = useState<string | null>(null)
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 })
  const [elapsed, setElapsed] = useState(0)
  const [errorDetail, setErrorDetail] = useState<{ phase: string; message: string } | null>(null)
  const [summary, setSummary] = useState<string | null>(null)
  const logEndRef = useRef<HTMLDivElement>(null)
  const startedAt = useRef<number>(0)
  /** Whether a terminal (done/error) event was actually observed on the stream. */
  const sawTerminal = useRef(false)

  function reset() {
    setPhase('idle')
    setLog([])
    setCurrent(null)
    setProgress({ done: 0, total: 0 })
    setElapsed(0)
    setErrorDetail(null)
    setSummary(null)
    sawTerminal.current = false
  }

  // Reset on (re-)open. Don't reset on close — preserves the done/error
  // summary during the close animation. With autoStart, kick off the op
  // immediately (the caller already confirmed) — run() overrides the idle phase.
  useEffect(() => {
    if (open) { reset(); if (autoStart) run() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (phase !== 'running') return
    const interval = setInterval(() => setElapsed(Date.now() - startedAt.current), 200)
    return () => clearInterval(interval)
  }, [phase])

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [log])

  function applyInterpretation(i: Interpretation) {
    if (i.log) setLog(l => [...l, i.log!])
    if (i.current !== undefined) setCurrent(i.current)
    if (i.progress) setProgress(i.progress)
    if (i.terminal) {
      sawTerminal.current = true
      setCurrent(null)
      if (i.terminal.kind === 'done') {
        setPhase('done')
        setSummary(i.terminal.summary ?? null)
        onComplete?.()
      } else {
        setPhase('error')
        setErrorDetail({ phase: i.terminal.phase, message: i.terminal.message })
      }
    }
  }

  async function run() {
    startedAt.current = Date.now()
    setPhase('running')

    let res: Response
    try {
      res = await fetch(request.url, {
        method: request.method,
        headers: request.body ? { 'content-type': 'application/json' } : undefined,
        body: request.body,
      })
    } catch (err) {
      setPhase('error')
      setErrorDetail({ phase: 'connect', message: err instanceof Error ? err.message : String(err) })
      return
    }
    // A non-OK response is a plain JSON error, not an NDJSON stream — e.g. a 409 for a
    // seed with no servable version, or a 500 on a malformed body. Reading it as a
    // stream yielded no recognizable terminal event, so the dialog sat at
    // phase='running' forever with both close paths blocked (an undismissable modal
    // that needed a page reload). Surface the body instead.
    if (!res.ok) {
      let message = `Request failed with HTTP ${res.status}`
      try {
        const text = await res.text()
        try {
          const parsed = JSON.parse(text) as { error?: string; message?: string }
          message = parsed.error || parsed.message || text || message
        } catch {
          if (text.trim()) message = text.trim()
        }
      } catch { /* keep the status-only message */ }
      setPhase('error')
      setErrorDetail({ phase: `http-${res.status}`, message })
      return
    }
    if (!res.body) {
      setPhase('error')
      setErrorDetail({ phase: 'connect', message: 'No response body from stream endpoint' })
      return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buf = ''
    try {
      while (true) {
        const { value, done } = await reader.read()
        if (value) buf += decoder.decode(value, { stream: true })
        if (done) break
        let nl
        while ((nl = buf.indexOf('\n')) !== -1) {
          const line = buf.slice(0, nl).trim()
          buf = buf.slice(nl + 1)
          if (!line) continue
          let ev: unknown
          try { ev = JSON.parse(line) } catch { continue }
          applyInterpretation(interpretEvent(ev))
        }
      }
      const tail = buf.trim()
      if (tail) {
        try { applyInterpretation(interpretEvent(JSON.parse(tail))) } catch { /* ignore */ }
      }
      // The stream ended without a done/error event — the operation's outcome is
      // unknown. Synthesize a terminal error rather than leaving the dialog running
      // and unclosable.
      if (!sawTerminal.current) {
        setCurrent(null)
        setPhase('error')
        setErrorDetail({
          phase: 'stream-eof',
          message: 'The stream ended before reporting a result. The operation may or may not have completed — reload and check before retrying.',
        })
      }
    } catch (err) {
      setPhase('error')
      setErrorDetail({ phase: 'stream-read', message: err instanceof Error ? err.message : String(err) })
    }
  }

  const running = phase === 'running'
  const pct = progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : 0

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !running) onClose() }}>
      <DialogContent className="max-w-2xl" onInteractOutside={(e) => { if (running) e.preventDefault() }}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {phase === 'idle' && (
          <>
            {warning && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{warning}</AlertDescription>
              </Alert>
            )}
            <div className="py-2 text-sm text-muted-foreground">
              Live progress will appear here once the operation starts. This cannot be undone.
            </div>
          </>
        )}

        {(running || phase === 'done' || phase === 'error') && (
          <div className="space-y-3 py-2">
            {progress.total > 0 && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>{progress.done} / {progress.total}</span>
                  <span>{(elapsed / 1000).toFixed(1)}s</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className={cn(
                      'h-full transition-all',
                      phase === 'error' ? 'bg-destructive' : phase === 'done' ? 'bg-emerald-500' : 'bg-primary',
                    )}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )}

            {current && running && (
              <div className="flex items-center gap-2 text-sm font-mono">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                <span className="text-foreground">{current}</span>
              </div>
            )}

            <div className="max-h-[260px] overflow-y-auto rounded border bg-muted/20 p-2 text-xs font-mono space-y-0.5">
              {log.length === 0 ? (
                <span className="text-muted-foreground">Starting…</span>
              ) : (
                log.map((entry, i) => (
                  <div
                    key={i}
                    className={cn(
                      entry.kind === 'ok' && 'text-emerald-700 dark:text-emerald-400',
                      entry.kind === 'error' && 'text-destructive',
                      entry.kind === 'info' && 'text-muted-foreground',
                    )}
                  >
                    {entry.text}
                  </div>
                ))
              )}
              <div ref={logEndRef} />
            </div>

            {phase === 'done' && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>{summary ?? `Done in ${(elapsed / 1000).toFixed(1)}s`}</AlertDescription>
              </Alert>
            )}

            {phase === 'error' && errorDetail && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="font-mono text-xs whitespace-pre-wrap break-all">
                  <strong>Failed during {errorDetail.phase}:</strong> {errorDetail.message}
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === 'idle' && (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button variant="destructive" onClick={run}>{confirmLabel}</Button>
            </>
          )}
          {running && (
            <Button variant="outline" disabled title="Operation still running on the server even if closed">
              Running…
            </Button>
          )}
          {(phase === 'done' || phase === 'error') && (
            <Button variant="outline" onClick={onClose}>
              <X className="h-3.5 w-3.5 mr-1" />
              Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Translate a `DeleteEvent` (from lib/ndjson-stream.ts on the worker side)
 * into an `Interpretation` for the dialog. Counts events by category and
 * accumulates them in a closure-scoped state so the progress bar can use
 * total-events-emitted as denominator until a `plan` event fires.
 */
export function makeDeleteInterpreter() {
  let workers = 0
  let r2Prefixes = 0
  let dos = 0
  let phase: string | null = null
  let plannedTotal = 0
  let stepsDone = 0

  return function interpret(raw: unknown): Interpretation {
    const ev = raw as { event: string; [k: string]: unknown }
    switch (ev.event) {
      case 'start':
        return { log: { kind: 'info', text: `Starting ${ev.subject}…` } }
      case 'plan': {
        // workers/versions/shares can describe the SAME set of items from
        // different angles (e.g. prune: workers=42 and versions=42 are the
        // same 42 operations). Use max to avoid double-counting in the
        // progress denominator. stepsDone only ticks on worker_delete, so
        // max(workers, versions) is the right ceiling.
        const total = Math.max(
          (ev.workers as number) || 0,
          (ev.versions as number) || 0,
          (ev.shares as number) || 0,
        )
        plannedTotal = total
        return {
          log: {
            kind: 'info',
            text: `Plan: ${ev.workers ?? 0} worker(s), ${ev.shares ?? 0} share(s), ${ev.versions ?? 0} version(s)`,
          },
          progress: { done: 0, total: plannedTotal },
        }
      }
      case 'phase': {
        phase = ev.name as string
        return { current: `Phase: ${phase}` }
      }
      case 'worker_delete': {
        workers++
        stepsDone++
        const result = ev.result as string
        const ok = result === 'deleted' || result === 'not-found'
        return {
          log: {
            kind: ok ? 'ok' : 'error',
            text: `${ok ? '✓' : '✗'} worker ${ev.name} → ${result}${ev.message ? ` (${ev.message})` : ''}`,
          },
          current: `Deleting workers (${workers})`,
          progress: plannedTotal > 0 ? { done: stepsDone, total: plannedTotal } : undefined,
        }
      }
      case 'r2_prefix_delete': {
        r2Prefixes++
        return {
          log: {
            kind: 'ok',
            text: `✓ r2 ${ev.prefix}  (${ev.objects} object${ev.objects === 1 ? '' : 's'})`,
          },
          current: `R2 prefixes cleared: ${r2Prefixes}`,
        }
      }
      case 'do_wipe': {
        dos++
        return {
          log: {
            kind: 'ok',
            text: `✓ DO wipe share=${ev.shareId} version=${ev.version}`,
          },
          current: `DOs wiped: ${dos}`,
        }
      }
      case 'd1_delete':
        return {
          log: {
            kind: 'ok',
            text: `✓ d1 ${ev.table}  (${ev.rows} row${ev.rows === 1 ? '' : 's'})`,
          },
        }
      case 'done': {
        const c = ev.counts as { workers: number; r2Objects: number; dos: number; d1Rows: number }
        return {
          terminal: {
            kind: 'done',
            summary: `Done in ${(((ev.durationMs as number) ?? 0) / 1000).toFixed(1)}s · ${c.workers} worker(s) · ${c.r2Objects.toLocaleString()} R2 object(s) · ${c.dos} DO(s) · ${c.d1Rows.toLocaleString()} D1 row(s)`,
          },
        }
      }
      case 'error':
        return {
          terminal: {
            kind: 'error',
            phase: (ev.phase as string) ?? 'unknown',
            message: (ev.message as string) ?? 'unknown error',
          },
        }
      default:
        return {}
    }
  }
}
